import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash, LockKey, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";

export default function Perfil() {
  const { user } = useAuth();
  const [form, setForm] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [show, setShow] = useState({ old: false, next: false, confirm: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.new_password.length < 8) {
      setErr("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (form.new_password !== form.confirm_password) {
      setErr("La confirmación no coincide con la nueva contraseña.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: form.old_password,
        new_password: form.new_password,
      });
      toast.success("Contraseña actualizada");
      setForm({ old_password: "", new_password: "", confirm_password: "" });
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-2xl" data-testid="perfil-page">
      <PageHeader kicker="Mi cuenta" title="Perfil" subtitle="Datos de tu cuenta y seguridad de acceso." />

      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="perfil-info">
        <div className="flex items-center gap-3 mb-1">
          <UserCircle size={22} weight="duotone" className="text-slate-700" />
          <h2 className="font-display font-bold text-lg text-ink">Datos de la cuenta</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
          <div>
            <div className="label-uppercase mb-1">Nombre</div>
            <div className="font-medium text-ink" data-testid="perfil-nombre">{user?.name}</div>
          </div>
          <div>
            <div className="label-uppercase mb-1">Correo</div>
            <div className="font-medium text-ink" data-testid="perfil-email">{user?.email}</div>
          </div>
          <div>
            <div className="label-uppercase mb-1">Rol</div>
            <div className="font-medium text-ink">{ROLE_LABELS[user?.role] || user?.role}</div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-3 mb-4">
          <LockKey size={22} weight="duotone" className="text-slate-700" />
          <h2 className="font-display font-bold text-lg text-ink">Cambiar contraseña</h2>
        </div>
        <form onSubmit={submit} className="space-y-4" data-testid="cambiar-password-form">
          <div className="space-y-1">
            <Label htmlFor="old_password" className="label-uppercase">Contraseña actual</Label>
            <div className="relative">
              <Input
                id="old_password"
                type={show.old ? "text" : "password"}
                value={form.old_password}
                onChange={(e) => setForm({ ...form, old_password: e.target.value })}
                required
                data-testid="perfil-old-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow({ ...show, old: !show.old })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ink"
                tabIndex={-1}
              >
                {show.old ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="new_password" className="label-uppercase">Nueva contraseña</Label>
            <div className="relative">
              <Input
                id="new_password"
                type={show.next ? "text" : "password"}
                value={form.new_password}
                onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                required
                minLength={8}
                data-testid="perfil-new-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow({ ...show, next: !show.next })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ink"
                tabIndex={-1}
              >
                {show.next ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="confirm_password" className="label-uppercase">Confirmar nueva contraseña</Label>
            <div className="relative">
              <Input
                id="confirm_password"
                type={show.confirm ? "text" : "password"}
                value={form.confirm_password}
                onChange={(e) => setForm({ ...form, confirm_password: e.target.value })}
                required
                minLength={8}
                data-testid="perfil-confirm-password"
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShow({ ...show, confirm: !show.confirm })}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ink"
                tabIndex={-1}
              >
                {show.confirm ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md" data-testid="perfil-error">
              {err}
            </div>
          )}
          <Button
            type="submit"
            disabled={busy}
            className="bg-brand hover:bg-brand-hover"
            data-testid="perfil-submit"
          >
            {busy ? "Guardando…" : "Actualizar contraseña"}
          </Button>
        </form>
      </div>
    </div>
  );
}
